import React, { useMemo, useCallback, useRef } from "react";
import { NavLink, useHistory } from "react-router-dom";
import {
    Box, Button, FormControlLabel, MenuItem, SelectField,
    Stack, Switch, TextField, Typography, InputAdornment, Icons, DateTimeStandard
} from "@curves/ui-base";
import { ActionMenu, OverflowMenu, Overview, ProductInfo } from "@curves/ui-shared";
import { CLUB_INVENTORY_ROUTER } from "@clubInventory/router/routerPath";
import { StockDialogBox } from "@clubInventory/stock/dialog";
import { ShipmentDialogBox } from "@clubInventory/shipment/dialog";
import { parseDate } from "@util/moment";
import { callRef, formatCurrencyFn, isFunction, ensureArray, refIsRequiredError } from "@util/utils";
import useOverview from "@util/hook/useOverview";
import useOptions from "@util/hook/useOptions";
import useClubInventoryApi from "@apis/useClubInventoryApi";

const toBoolean = value => value === true || value === 1 || value === "1" || value === "true";

const tableHeaderRow = [
    {
        cellKey: "hasPendingDefective",
        className: "px-0",
        headerLabel: "",
        formatCell(hasPendingDefective, row, { onDefectIndicatorClick }) {
            if (!hasPendingDefective || !row) return null;
            return (
                <Box
                    className="stock-overview-defect-indicator"
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        // 點擊瑕疵指示器：只處理動作，不觸發列點擊/導頁
                        isFunction(onDefectIndicatorClick) && onDefectIndicatorClick(row);
                    }}
                />
            );
        },
    },
    {
        cellKey: "productName",
        headerLabel: "商品名稱",
        minWidth: 200,
        formatCell(productName, row) {
            return (
                <NavLink to={CLUB_INVENTORY_ROUTER.STOCK.GET_DETAIL_PATH(row.productID, row.variantID)} className="stock-overview-product-link">
                    <ProductInfo
						width={320}
                        product={{
                            sku: row.sku,
                            name: row.productName,
                            spec: row.variantName ?? "",
                        }}
                    />
                </NavLink>
            );
        },
    },
    { cellKey: "categoryName", headerLabel: "分類" },
    {
        cellKey: "defaultSalePrice",
        headerLabel: "預設販售價格",
        align: "center",
        formatCell: value => formatCurrencyFn(value, { symbol: "$" }),
    },
    {
        cellKey: "quantity",
        align: "center",
        headerLabel: "可販售數量",
        formatCell(quantity, row, { onAdjustQtyClick }) {
            if (row == null) return quantity;
            const canAdjust = row?.hasQuantityAdjustmentOrder === true;
            return (
                <Stack
                    className="stock-overview-qty-cell"
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    onClick={
                        canAdjust
                            ? e => {
                                e.preventDefault();
                                e.stopPropagation();
                                // 點擊數量欄位：開「庫存數量調整」Dialog（避免導頁）
                                isFunction(onAdjustQtyClick) && onAdjustQtyClick(row);
                            }
                            : undefined
                    }
                    style={{ cursor: canAdjust ? "pointer" : "default" }}
                >
					<span className="stock-overview-qty-wrapper">
						{quantity}
                        {canAdjust && <Icons.Exclamation className="stock-overview-qty-icon" />}
					</span>
                </Stack>
            );
        },
    },
    {
        cellKey: "lastCountDate",
        align: "center",
        headerLabel: "上一次盤點日",
        formatCell: value => parseDate(value, DateTimeStandard.DisplayDateBySlash),
    },
    {
        cellKey: "stockID",
        align: "center",
        headerLabel: "",
        formatCell(_, row, { openStockDetail, onAdjustQtyClick, onConvertToDefect, onDefectClaim, onDefectStockIn, onGiftClaim }) {
            if (row == null) return null;
            return (
                <OverflowMenu
                    enabled
                    menuData={[
                        { text: "查看庫存明細", onClick: () => isFunction(openStockDetail) && openStockDetail(row) },
                        row.hasQuantityAdjustmentOrder && { text: "庫存數量調整", onClick: () => isFunction(onAdjustQtyClick) && onAdjustQtyClick(row) },
                        { text: "瑕疵回報", onClick: () => isFunction(onConvertToDefect) && onConvertToDefect(row) },
                        row.hasPendingDefective && { text: "瑕疵領用", onClick: () => isFunction(onDefectClaim) && onDefectClaim(row) },
                        row.hasPendingDefective && { text: "瑕疵入庫", onClick: () => isFunction(onDefectStockIn) && onDefectStockIn(row) },
                        { text: "贈品領用", onClick: () => isFunction(onGiftClaim) && onGiftClaim(row) },
                    ].filter(Boolean)}
                />
            );
        },
    },
];

const DEFAULT_SEARCH_PARAMS = {
    keyword: "",
    isDefective: false,
    saleCategoryID: 0,
    pageIndex: 1,
    pageSize: 20,
};

// 功能：庫存總覽（搜尋、列表、開啟各種 Dialog）
const StockOverviewPage = () => {
    const history = useHistory();
    const dialogBoxRef = useRef(null);
    const shipmentDialogBoxRef = useRef(null);
    const { getClubInventoryOptionsApi, postClubInventoryOverviewApi } = useClubInventoryApi();

    // 功能：搜尋欄位 ref（用於必填驗證與取值）
    const keywordRef = useRef(null);
    const categoryRef = useRef(null);
    const isDefectiveRef = useRef(null);

    const { options } = useOptions({
        getOptionsApi: getClubInventoryOptionsApi,
        isFetchOnMount: true,
    });

    const { overviewData, searchParams, getOverviewData, updateOverviewPage } = useOverview({
        getOverviewApi: postClubInventoryOverviewApi,
        defaultSearchParams: DEFAULT_SEARCH_PARAMS,
        isFetchOnMount: true,
    });

    // 功能：Dialog 成功後刷新（保留當前 searchParams）
    const doRefresh = useCallback(() => getOverviewData(searchParams), [getOverviewData, searchParams]);

    // 功能：開啟商品盤點 Dialog
    const handleOpenProductInventory = useCallback(() => {
        const { openProductInventoryDialog } = dialogBoxRef.current ?? {};
        if (isFunction(openProductInventoryDialog)) openProductInventoryDialog(null, doRefresh);
    }, [doRefresh]);

    // 功能：開啟商品領用 Dialog
    const handleOpenProductClaim = useCallback(() => {
        const { openProductClaimDialog } = dialogBoxRef.current ?? {};
        if (isFunction(openProductClaimDialog)) openProductClaimDialog(null, doRefresh);
    }, [doRefresh]);

    // 功能：開啟瑕疵領用 Dialog
    const handleOpenDefectClaim = useCallback(
        row => {
            const { openDefectClaimDialog } = dialogBoxRef.current ?? {};
            if (isFunction(openDefectClaimDialog)) {
                openDefectClaimDialog(
                    {
                        productID: row?.productID,
                        variantID: row?.variantID ?? 0,
                    },
                    doRefresh,
                );
            }
        },
        [doRefresh],
    );

    // 功能：開啟瑕疵入庫 Dialog
    const handleOpenDefectStockIn = useCallback(
        row => {
            const { openDefectStockInDialog } = dialogBoxRef.current ?? {};
            if (isFunction(openDefectStockInDialog)) {
                openDefectStockInDialog(
                    {
                        productID: row?.productID,
                        variantID: row?.variantID ?? 0,
                    },
                    doRefresh,
                );
            }
        },
        [doRefresh],
    );

    // 功能：開啟庫存數量調整 Dialog
    const handleOpenAdjustStockQty = useCallback(
        row => {
            const { openAdjustStockQtyDialog } = dialogBoxRef.current ?? {};
            if (isFunction(openAdjustStockQtyDialog)) {
                openAdjustStockQtyDialog(
                    {
                        productID: row?.productID,
                        variantID: row?.variantID ?? 0,
                    },
                    doRefresh,
                );
            }
        },
        [doRefresh],
    );

    // 功能：開啟瑕疵回報 Dialog（帶入商品識別/顯示用資訊）
    // 若勾選「同步執行退貨流程」且回報成功，由 onSyncReturnFlow 開啟退貨單彈窗
    const handleOpenConvertToDefect = useCallback(
        row => {
            const { openConvertToDefectDialog } = dialogBoxRef.current ?? {};
            if (isFunction(openConvertToDefectDialog)) {
                openConvertToDefectDialog(
                    {
                        productID: row?.productID,
                        variantID: row?.variantID ?? 0,
                    },
                    doRefresh,
                    () => shipmentDialogBoxRef.current?.openDefectReturnDialog(null, doRefresh),
                );
            }
        },
        [doRefresh],
    );

    // 功能：開啟贈品領用 Dialog
    const handleOpenGiftClaim = useCallback(
        row => {
            const { openGiftClaimDialog } = dialogBoxRef.current ?? {};
            if (isFunction(openGiftClaimDialog)) {
                openGiftClaimDialog(row, doRefresh);
            }
        },
        [doRefresh],
    );

    // 功能：組 overview 查詢參數（對齊 PurchaseOverviewPage：只從 ref/state 取值，不讀 form）
    const getParamsData = useCallback(
        () => ({
            ...searchParams,
            keyword: callRef(keywordRef, "getResult") || "",
            isDefective: Boolean(isDefectiveRef.current?.checked),
            saleCategoryID: callRef(categoryRef, "getResult") || "0",
            pageIndex: 1,
        }),
        [searchParams],
    );

    // 功能：搜尋送出（必填驗證通過才回傳 params）
    const handleSearchSubmit = useCallback(() => {
        const isError = refIsRequiredError(keywordRef, categoryRef);
        if (isError) return false;
        const paramsData = getParamsData();
        return paramsData;
    }, [getParamsData]);

    // 功能：導頁至庫存明細頁
    const openStockDetail = useCallback(row => history.push(CLUB_INVENTORY_ROUTER.STOCK.GET_DETAIL_PATH(row?.productID, row?.variantID)), [history]);

    const actionMenuData = useMemo(
        () => [
            { text: "商品盤點", onClick: handleOpenProductInventory },
            { text: "商品領用", onClick: handleOpenProductClaim },
        ],
        [handleOpenProductInventory, handleOpenProductClaim],
    );

    const contextValue = useMemo(
        () => ({
            overviewData,
            searchParams,
            getOverviewData,
            updateOverviewPage,
        }),
        [overviewData, searchParams, getOverviewData, updateOverviewPage],
    );

    const cellRenderContext = useMemo(
        () => ({
            onDefectIndicatorClick: handleOpenDefectClaim,
            onAdjustQtyClick: handleOpenAdjustStockQty,
            onConvertToDefect: handleOpenConvertToDefect,
            onDefectClaim: handleOpenDefectClaim,
            onDefectStockIn: handleOpenDefectStockIn,
            onGiftClaim: handleOpenGiftClaim,
            openStockDetail,
        }),
        [handleOpenDefectClaim, handleOpenAdjustStockQty, handleOpenConvertToDefect, handleOpenDefectStockIn, handleOpenGiftClaim, openStockDetail],
    );

    return (
        <Box className="px-3 py-2">
            {/* 標題與建立按鈕 */}
            <Stack justifyContent="space-between" alignItems="center" className="mb-2">
                <Typography variant="h5" className="mb-0 font-weight-bold">
                    商品庫存總覽
                </Typography>
                <ActionMenu buttonText="建立" menuData={actionMenuData} />
            </Stack>

            <Overview contextValue={contextValue} idKey="stockID">
                {/* 搜尋區 */}
                <Overview.Search className="p-3 mb-3" getSearchParamsOnSubmit={handleSearchSubmit}>
                    <Stack alignItems="center" spacing={2}>
                        <TextField
                            ref={keywordRef}
                            key={searchParams.keyword ?? ""}
                            defaultValue={searchParams.keyword}
                            inputProps={{ placeholder: "請輸入商品名稱或商品SKU" }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icons.Magnifier />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormControlLabel
                            key={String(searchParams.isDefective)}
                            control={<Switch defaultChecked={toBoolean(searchParams.isDefective)} inputProps={{ ref: isDefectiveRef }} />}
                            prefixLabel="只顯示瑕疵商品"
                        />
                        <Button variant="contained" type="submit">
                            搜尋
                        </Button>
                    </Stack>
                </Overview.Search>

                {/* 分類篩選與筆數 */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" className="mb-2">
                    <SelectField
                        ref={categoryRef}
                        key={String(searchParams.saleCategoryID ?? "0")}
                        defaultValue={searchParams.saleCategoryID ?? "0"}
                        onChange={e =>
                            getOverviewData({
                                ...searchParams,
                                saleCategoryID: e.target.value || "0",
                                pageIndex: 1,
                            })
                        }
                        required
                    >
                        {ensureArray(options?.categoryOptions).map(({ text, value }) => (
                            <MenuItem key={value} value={value}>
                                {text}
                            </MenuItem>
                        ))}
                    </SelectField>
                    <Overview.TotalCount className="mb-0" showCurrentCount />
                </Stack>

                <Overview.Table className="mb-3" headerRow={tableHeaderRow} cellRenderContext={cellRenderContext} />
                <Overview.Pagination />
            </Overview>

            <StockDialogBox ref={dialogBoxRef} />
            <ShipmentDialogBox ref={shipmentDialogBoxRef} />
        </Box>
    );
};

export default StockOverviewPage;
